/**
 * Tipos das telas de TESOURARIA/BANCOS, espelhando os DTOs reais do backend
 * (backend/apps/api/src/features/finance/presentation/http/dto/treasury.dto.ts).
 *
 * Regra: nunca inventar campo. Cada interface abaixo é o espelho campo a campo
 * do DTO correspondente; o que o backend não publica (ex.: "status" por item
 * do fluxo diário) fica de fora — a tela deriva o que pode e rotula a derivação.
 *
 * Contratos consumidos:
 *   GET  /finance/flow/daily?days=7            → DailyFlowDto
 *   GET  /finance/payments?page&pageSize        → PaginatedResponseDto<ScheduledPaymentResponseDto>
 *   POST /finance/payments                      → ScheduledPaymentResponseDto (SchedulePaymentDto)
 *   POST /finance/payments/:id/transmit         → ScheduledPaymentResponseDto (TransmitPaymentDto { batchId })
 *   POST /finance/payments/:id/confirm          → ConfirmFinancialEntryResultDto (ConfirmFinancialEntryDto)
 *   GET  /finance/bank-tariffs?channel=         → PaginatedResponseDto<BankTariffResponseDto>
 *   POST /finance/bank-tariffs                  → BankTariffResponseDto (BankTariffInputDto)
 *   PUT  /finance/bank-tariffs/:id              → BankTariffResponseDto (BankTariffUpdateInputDto)
 *   DELETE /finance/bank-tariffs/:id            → { id, deleted }
 *   GET  /finance/mdr-rates?brand&isActive      → PaginatedResponseDto<MdrRateResponseDto>
 *   POST /finance/mdr-rates                     → MdrRateResponseDto (MdrRateInputDto)
 *   PUT  /finance/mdr-rates/:id                 → MdrRateResponseDto (MdrRateUpdateInputDto)
 *   DELETE /finance/mdr-rates/:id               → { id, deleted }
 */

/* ── Envelope paginado (finance.dto.ts: PaginatedResponseDto) ── */

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/* ── Fluxo de caixa diário (treasury.dto.ts: DailyFlowDto) ── */

export type FlowSide = 'INFLOW' | 'OUTFLOW';

/** treasury.dto.ts: DailyFlowItemDto */
export interface DailyFlowItem {
  id: string;
  date: string;
  type: string;
  side: FlowSide;
  description: string;
  bankAccount: string;
  accountCode: string;
  accountingReference?: string | null;
  value: number;
}

/** treasury.dto.ts: DailyFlowDayDto */
export interface DailyFlowDay {
  date: string;
  inflow: number;
  outflow: number;
  net: number;
  items: DailyFlowItem[];
}

/** treasury.dto.ts: DailyFlowDto */
export interface DailyFlow {
  days: DailyFlowDay[];
  totalInflow: number;
  totalOutflow: number;
  net: number;
}

/* ── Pagamentos CNAB/API (treasury.dto.ts: ScheduledPaymentResponseDto) ── */

export type PaymentChannel = 'CNAB' | 'API';
export type PaymentStatus = 'SCHEDULED' | 'TRANSMITTED' | 'CONFIRMED' | 'FAILED' | 'CANCELLED';

/** treasury.dto.ts: PaymentLogEntryDto */
export interface PaymentLogEntry {
  at: string;
  event: string;
  message?: string;
}

/** treasury.dto.ts: ScheduledPaymentResponseDto */
export interface ScheduledPayment {
  id: string;
  companyId: string;
  payableId?: string | null;
  batchId?: string | null;
  beneficiaryName: string;
  beneficiaryDocument?: string | null;
  documentNumber?: string | null;
  dueDate: string;
  amount: number;
  paymentMethod: string;
  channel: PaymentChannel;
  status: PaymentStatus;
  log: PaymentLogEntry[];
  transmittedAt?: string | null;
  confirmedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** treasury.dto.ts: SchedulePaymentDto (corpo do POST /finance/payments) */
export interface SchedulePaymentInput {
  companyId: string;
  payableId?: string;
  beneficiaryName: string;
  beneficiaryDocument?: string;
  documentNumber?: string;
  dueDate: string;
  amount: number;
  paymentMethod: string;
  channel?: PaymentChannel;
}

/** treasury.dto.ts: TransmitPaymentDto (corpo do POST /finance/payments/:id/transmit) */
export interface TransmitPaymentInput {
  batchId: string;
}

/** treasury.dto.ts: ConfirmFinancialEntryDto */
export interface ConfirmFinancialEntryInput {
  accountId: string;
  notes?: string;
}

/** treasury.dto.ts: ConfirmFinancialEntryResultDto */
export interface ConfirmFinancialEntryResult {
  payment: ScheduledPayment;
  payableSettled: boolean;
  transactionId?: string | null;
  newBalance?: number | null;
  newStatus?: string | null;
}

/* ── Tarifas bancárias (treasury.dto.ts: BankTariffResponseDto) ── */

export type BankTariffChannel = 'GENERAL' | 'CARD' | 'PIX' | 'TRANSFER';
export type ChargeType = 'FIXED' | 'PERCENTAGE';

/** treasury.dto.ts: BankTariffResponseDto */
export interface BankTariff {
  id: string;
  service: string;
  channel: BankTariffChannel;
  modality?: string | null;
  brand?: string | null;
  chargeType: ChargeType;
  currentValue: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** treasury.dto.ts: BankTariffInputDto (corpo do POST /finance/bank-tariffs) */
export interface BankTariffInput {
  service: string;
  channel?: BankTariffChannel;
  modality?: string;
  brand?: string;
  chargeType?: ChargeType;
  currentValue: number;
}

/** treasury.dto.ts: BankTariffUpdateInputDto (corpo do PUT /finance/bank-tariffs/:id) */
export type BankTariffUpdateInput = Partial<
  Pick<BankTariffInput, 'service' | 'modality' | 'brand' | 'chargeType' | 'currentValue'>
> & {
  isActive?: boolean;
};

/** treasury.dto.ts: BankTariffFiltersDto (query string de GET /finance/bank-tariffs) */
export interface BankTariffFilters {
  channel?: BankTariffChannel;
  brand?: string;
  service?: string;
  isActive?: boolean;
  page?: number;
  pageSize?: number;
}

/* ── MDR — taxas de operadoras (treasury.dto.ts: MdrRateResponseDto) ── */

/** treasury.dto.ts: MdrRateResponseDto */
export interface MdrRate {
  id: string;
  brand: string;
  debitRate: number;
  creditCashRate: number;
  installments2To6Rate: number;
  installments7To12Rate: number;
  anticipationRate?: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** treasury.dto.ts: MdrRateInputDto (corpo do POST /finance/mdr-rates) */
export interface MdrRateInput {
  brand: string;
  debitRate?: number;
  creditCashRate?: number;
  installments2To6Rate?: number;
  installments7To12Rate?: number;
  anticipationRate?: number;
}

/** treasury.dto.ts: MdrRateUpdateInputDto */
export type MdrRateUpdateInput = Partial<MdrRateInput> & {
  isActive?: boolean;
};

/** treasury.dto.ts: MdrRateFiltersDto */
export interface MdrRateFilters {
  brand?: string;
  isActive?: boolean;
  page?: number;
  pageSize?: number;
}

/* ── Rótulos de apresentação (derivados do backend, para exibição) ── */

export const BANK_TARIFF_CHANNEL_LABEL: Record<BankTariffChannel, string> = {
  GENERAL: 'Gerais',
  CARD: 'Cartões',
  PIX: 'Pix',
  TRANSFER: 'Transferências',
};

export const CHARGE_TYPE_LABEL: Record<ChargeType, string> = {
  FIXED: 'Fixo por Unidade',
  PERCENTAGE: 'Percentual por Venda',
};

export const PAYMENT_STATUS_LABEL: Record<PaymentStatus, string> = {
  SCHEDULED: 'Agendado',
  TRANSMITTED: 'Transmitido',
  CONFIRMED: 'Confirmado',
  FAILED: 'Falhou',
  CANCELLED: 'Cancelado',
};

export const PAYMENT_CHANNEL_LABEL: Record<PaymentChannel, string> = {
  CNAB: 'CNAB',
  API: 'API',
};

export const FLOW_SIDE_LABEL: Record<FlowSide, string> = {
  INFLOW: 'Entrada',
  OUTFLOW: 'Saída',
};